/**
 * User-facing copy for an association cardinality violation returned by the
 * events API on submit. Names in the message open the conflicting record's
 * slideout so the user can fix it without leaving the form.
 */

import type { ReactNode } from 'react';

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { CardinalityConflict } from '@/lib/crm/core/types';
import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';

import { ConflictName } from './ConflictName';

export interface CardinalityErrorDetail {
  association_type_name?: string;
  direction?: string;
  conflicts?: CardinalityConflict[];
}

export function buildCardinalityErrorMessage(
  detail: CardinalityErrorDetail,
  associationTypes: AssociationType[],
  onOpenSlideout: (entityId: string) => void
): ReactNode {
  const assocType = detail.association_type_name
    ? associationTypes.find(
        a => a.association_type_name === detail.association_type_name
      )
    : null;

  const sourceConfig = assocType
    ? getEntityTypeConfig(assocType.source_entity_type_name)
    : null;
  const targetConfig = assocType
    ? getEntityTypeConfig(assocType.target_entity_type_name)
    : null;
  const sourceName = (sourceConfig?.displayName ?? 'record').toLowerCase();
  const sourcePluralName = (
    sourceConfig?.pluralDisplayName ?? 'records'
  ).toLowerCase();
  const targetName = (targetConfig?.displayName ?? 'record').toLowerCase();

  const conflicts = detail.conflicts ?? [];

  // Multiple conflicts — render a list with clickable names
  if (conflicts.length > 1) {
    return (
      <div>
        <p>
          {conflicts.length} {sourcePluralName} are already associated with a{' '}
          {targetName}. A {sourceName} can only be linked to one {targetName} at
          a time.
        </p>
        <p className='mt-2 font-medium'>Impacted {sourcePluralName}:</p>
        <ul className='mt-1 list-disc pl-5 space-y-0.5'>
          {conflicts.map(conflict => (
            <li key={conflict.entity_id}>
              <ConflictName
                conflict={conflict}
                onOpenSlideout={onOpenSlideout}
              />
              {conflict.existing_owner?.display_name && (
                <span className='text-utility-gray-500'>
                  {` – currently associated with ${conflict.existing_owner.display_name}`}
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className='mt-2'>
          Edit each {sourceName} to remove or change their existing {targetName}{' '}
          association, then try again.
        </p>
      </div>
    );
  }

  // Single conflict — show inline message, still with clickable name if available
  if (conflicts.length === 1 && assocType) {
    const conflict = conflicts[0];
    const ownerName = conflict.existing_owner?.display_name;
    return (
      <div>
        <p>
          {'The selected '}
          <ConflictName conflict={conflict} onOpenSlideout={onOpenSlideout} />
          {ownerName
            ? ` is already associated with ${ownerName}. `
            : ` is already associated with a ${targetName}. `}
          {`A ${sourceName} can only be linked to one ${targetName} at a time.`}
        </p>
        <p className='mt-2'>
          {`To fix this, edit the ${sourceName} to remove or change their existing ${targetName} association, then try again.`}
        </p>
      </div>
    );
  }

  // Fallback — no conflicts array (legacy) or no association type info
  if (assocType) {
    return (
      `The selected ${sourceName} is already associated with a ${targetName}. ` +
      `A ${sourceName} can only be linked to one ${targetName} at a time.\n\n` +
      `To fix this, edit the ${sourceName} to remove or change their existing ${targetName} association, then try again.`
    );
  }

  return (
    'The selected record is already associated with another record of this type. ' +
    'Remove the existing association first, then try again.'
  );
}
